import { Fragment, useRef, useState } from "react";
import {
  ExternalLinkIcon,
  LinkIcon,
  MoreHorizontalIcon,
  UserIcon,
  XIcon,
} from "lucide-react";
import { DateRange } from "react-day-picker";
import { useQueryClient } from "@tanstack/react-query";

import {
  Column,
  EmptyDataDash,
  ErrorText,
  LoadingIndicator,
  PlacementStatusBadge,
  Row,
  Text,
} from "@/components";
import { CallWithTranscript } from "@/components/CallWithTranscript";
import { InternalSourceBadge } from "@/components/InternalSourceBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useUpdateCall } from "@/hooks/mutations/useUpdateCall";
import { useUpdateCandidacyStatusChange } from "@/hooks/mutations/useUpdateCandidacyStatusChange";
import { useUpdatePlacementById } from "@/hooks/mutations/useUpdatePlacementById";
import { useGetCallSignedAudioUrl } from "@/hooks/queries/useGetCallSignedAudioUrl";
import { useGetLeaderboardDetail } from "@/hooks/queries/useGetLeaderboardDetail";
import { useGetOtherCandidacies } from "@/hooks/queries/useGetOtherCandidacies";
import {
  callStatusToBadgeClasses,
  callStatusToLabel,
  dateFilterToLabel,
  timezoneToIana,
  timezoneToLabel,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { formatting } from "@/lib/formatting";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import {
  CallDetailRecord,
  CallDirection,
  CallStatus,
  DateFilterOption,
  LeaderboardDetailRecord,
  LeaderboardDetailRecordType,
  LeaderboardMetric,
  OfferDetailRecord,
  OfferOutcome,
  PlacementDetailRecord,
  StatusChangeDetailRecord,
} from "@/types";

type StafferOptions = { label: string; value: number }[];

const formatDate = (iso: string) => dates.formatIsoDateTimeForUser(iso) ?? "";

const formatDateOnly = (iso: string) => dates.formatIsoDateForUser(iso) ?? "";

const formatInterviewDate = (record: StatusChangeDetailRecord) => {
  const iso = record.interviewDateTime ?? record.date;
  const ianaTz = record.interviewTimezone
    ? timezoneToIana[record.interviewTimezone]
    : undefined;
  const formatted = new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: ianaTz,
  });
  const tzLabel = record.interviewTimezone
    ? timezoneToLabel[record.interviewTimezone]
    : null;
  return tzLabel ? `${formatted} ${tzLabel}` : formatted;
};

const formatDuration = (seconds: number | null) => {
  if (seconds === null) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString()}:${String(s).padStart(2, "0")}`;
};

const formatRole = (r: LeaderboardDetailRecord) => {
  if (r.companyName && r.jobTitle) return `${r.companyName} - ${r.jobTitle}`;
  if (r.companyName) return r.companyName;
  if (r.jobTitle) return r.jobTitle;
  return "—";
};

const openCandidacy = (record: LeaderboardDetailRecord, tab?: string) => {
  const { companyId, jobId, candidacyId } = record;
  if (!companyId || !jobId || !candidacyId) return;
  mixpanelUtils.trackButtonClick(
    "Open candidacy from detail sheet",
    MIXPANEL_SCREENS.LEADERBOARD,
  );
  const params = new URLSearchParams({ c: candidacyId });
  if (tab) params.set("pt", tab);
  window.open(
    `/companies/${companyId.toString()}/jobs/${jobId.toString()}?${params.toString()}`,
    "_blank",
  );
};

const CallDetailPanel = ({
  record,
  onClose,
}: {
  record: CallDetailRecord;
  onClose: () => void;
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const { data: audioSrc, status } = useGetCallSignedAudioUrl({
    callId: String(record.id),
  });

  return (
    <Column className="w-[400px] shrink-0 gap-4 border-l border-slate-200 p-4 duration-200 animate-in fade-in slide-in-from-right-4">
      <Row className="items-center justify-between">
        <Column className="gap-0.5">
          <p className="text-sm font-semibold text-slate-700">
            {record.candidateName ?? "Unknown"}
          </p>
          <p className="text-xs text-slate-500">{formatDate(record.date)}</p>
        </Column>
        <Button
          size="icon"
          variant="ghost"
          onClick={() => {
            mixpanelUtils.trackButtonClick(
              "Close detail sheet",
              MIXPANEL_SCREENS.LEADERBOARD,
            );
            onClose();
          }}
        >
          <XIcon className="h-4 w-4" />
        </Button>
      </Row>
      <Column className="flex-1 overflow-auto">
        {record.transcript && record.transcript.transcriptLines.length > 0 ? (
          status === "pending" ? (
            <LoadingIndicator className="py-8" variant="small" />
          ) : status === "error" || !audioSrc ? (
            <ErrorText
              className="text-sm"
              errorMessage="Unable to load audio."
            />
          ) : (
            <CallWithTranscript
              audioRef={audioRef}
              audioSrc={audioSrc}
              downloadFilename={`call-${record.candidateName ?? "unknown"}.mp3`
                .toLowerCase()
                .replace(/\s+/g, "-")}
              getRoleLabel={(role) =>
                role === "staffer" ? "Staffer" : "Candidate"
              }
              transcriptLines={record.transcript.transcriptLines}
              defaultOpen
            />
          )
        ) : (
          <p className="text-sm text-slate-500">No transcript available.</p>
        )}
      </Column>
    </Column>
  );
};

const CallStatusBadge = ({ status }: { status: CallStatus }) => {
  return (
    <Badge className={callStatusToBadgeClasses[status]}>
      {callStatusToLabel[status]}
    </Badge>
  );
};

const OwnerAvatar = ({
  ownerUserId,
  stafferOptions,
}: {
  ownerUserId: number;
  stafferOptions: StafferOptions;
}) => {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const name = stafferOptions.find((s) => s.value === ownerUserId)?.label || "";
  const initials = name
    .split(" ")
    .map((part) => part[0])
    .join("");
  return (
    <Row className="h-7 w-7 items-center justify-center rounded-full bg-slate-200">
      <Text className="text-[10px] text-slate-900">{initials}</Text>
    </Row>
  );
};

const AssignCandidacySubMenu = ({
  record,
  onAssign,
}: {
  record: CallDetailRecord;
  onAssign: (candidacyId: string) => void;
}) => {
  const { data: options, status } = useGetOtherCandidacies({
    candidacyId: record.candidacyId,
    talentId: record.talentId,
  });

  /* Render */
  const renderOptions = () => {
    // Without a talent id the lookup can't run, so there is nothing to assign.
    if (record.talentId == null) {
      return (
        <DropdownMenuItem disabled>No other candidacies found</DropdownMenuItem>
      );
    }
    if (status === "pending") {
      return <DropdownMenuItem disabled>Loading...</DropdownMenuItem>;
    }
    if (status === "error") {
      return (
        <DropdownMenuItem disabled>Unable to load options</DropdownMenuItem>
      );
    }
    if (options.length === 0) {
      return (
        <DropdownMenuItem disabled>No other candidacies found</DropdownMenuItem>
      );
    }

    return options.map((opt) => (
      <DropdownMenuItem
        key={opt.id}
        onClick={() => {
          onAssign(opt.id);
        }}
      >
        {opt.company.name} - {opt.job.title}
      </DropdownMenuItem>
    ));
  };

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <LinkIcon className="h-3.5 w-3.5" />
        Assign candidacy
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>{renderOptions()}</DropdownMenuSubContent>
    </DropdownMenuSub>
  );
};

const DialsTable = ({
  onAssignCandidacy,
  onReassign,
  records,
  showOwner,
  stafferOptions,
}: {
  onAssignCandidacy: (callId: string, candidacyId: string) => void;
  onReassign: (recordId: string, newUserId: number) => void;
  records: CallDetailRecord[];
  showOwner: boolean;
  stafferOptions: StafferOptions;
}) => {
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);

  const selectedRecord = selectedCallId
    ? (records.find((r) => r.id === selectedCallId) ?? null)
    : null;

  return (
    <Row className="min-h-0 flex-1">
      <Column className="min-w-0 flex-1 overflow-auto">
        <Table className="text-slate-700">
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Direction</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Candidate</TableHead>
              <TableHead>Role</TableHead>
              {showOwner && <TableHead>Owner</TableHead>}
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.map((r) => (
              <TableRow
                key={r.id}
                className={cn(
                  "cursor-pointer",
                  selectedCallId === r.id && "bg-slate-100",
                )}
                onClick={() => {
                  setSelectedCallId(selectedCallId === r.id ? null : r.id);
                }}
              >
                <TableCell>{formatDate(r.date)}</TableCell>
                <TableCell>
                  {r.direction === CallDirection.INCOMING ? (
                    <Badge className="border-blue-300 bg-blue-50 text-blue-700">
                      Incoming
                    </Badge>
                  ) : (
                    <Badge className="border-purple-300 bg-purple-50 text-purple-700">
                      Outgoing
                    </Badge>
                  )}
                </TableCell>
                <TableCell>{r.phone ?? <EmptyDataDash />}</TableCell>
                <TableCell>
                  <CallStatusBadge status={r.status} />
                </TableCell>
                <TableCell>{formatDuration(r.duration)}</TableCell>
                <TableCell>
                  <Row className="items-center gap-1.5">
                    {r.candidateName ?? <EmptyDataDash />}
                    <InternalSourceBadge source={r.candidacySource} />
                  </Row>
                </TableCell>
                <TableCell className="max-w-[200px]">
                  <Row className="items-center gap-1.5">
                    <span className="truncate">{formatRole(r)}</span>
                    {r.isEstimatedCandidacy && (
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <Badge className="shrink-0 border-amber-300 bg-amber-50 px-1.5 py-0 text-[10px] text-amber-700">
                                Est.
                              </Badge>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <p className="max-w-[240px] text-xs">
                              This candidacy was estimated based on the most
                              recently active candidacy at the time this call
                              was made. It is not guaranteed to be correct.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </Row>
                </TableCell>
                {showOwner && (
                  <TableCell>
                    <OwnerAvatar
                      ownerUserId={r.ownerUserId}
                      stafferOptions={stafferOptions}
                    />
                  </TableCell>
                )}
                <TableCell
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <RowActionsDropdown
                    onReassign={(newUserId) => {
                      onReassign(r.id, newUserId);
                    }}
                    record={r}
                    stafferOptions={stafferOptions}
                    tab="staffer-call"
                  >
                    {r.talentId ? (
                      <AssignCandidacySubMenu
                        onAssign={(candidacyId) => {
                          onAssignCandidacy(r.id, candidacyId);
                        }}
                        record={r}
                      />
                    ) : (
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <DropdownMenuItem disabled>
                                <LinkIcon className="h-3.5 w-3.5" />
                                Assign candidacy
                              </DropdownMenuItem>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="left">
                            <p className="max-w-[200px] text-xs">
                              Cannot assign candidacy. No full or pending user
                              is linked to this call.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </RowActionsDropdown>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Column>
      {selectedRecord && (
        <CallDetailPanel
          record={selectedRecord}
          onClose={() => {
            setSelectedCallId(null);
          }}
        />
      )}
    </Row>
  );
};

const PlacementFlagBadge = ({
  badgeClassName,
  explanation,
  label,
}: {
  badgeClassName: string;
  explanation: string;
  label: string;
}) => (
  <TooltipProvider delayDuration={0}>
    <Tooltip>
      <TooltipTrigger asChild>
        <span>
          <Badge
            className={cn("shrink-0 px-1.5 py-0 text-[10px]", badgeClassName)}
          >
            {label}
          </Badge>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">
        <Text className="max-w-[260px] text-xs font-normal text-slate-700">
          {explanation}
        </Text>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const BackfillBadge = ({ record }: { record: PlacementDetailRecord }) => {
  const backfillFor = record.backfilledCandidateName
    ? ` as a backfill for ${record.backfilledCandidateName}`
    : " as a backfill";
  const explanation = `Placed ${formatDateOnly(record.dateAccepted ?? record.date)}${backfillFor}, but counted on ${formatDateOnly(record.dateCredited ?? record.date)} — when the requisition was originally filled and its revenue earned.`;

  if (record.countsInPeriod) {
    return (
      <PlacementFlagBadge
        badgeClassName="border-amber-300 bg-amber-50 text-amber-700"
        explanation={explanation}
        label={`Backfilled ${dates.formatShortDate(record.dateAccepted ?? record.date)}`}
      />
    );
  }

  // Greyed context row: the chip says where the placement actually counted
  return (
    <PlacementFlagBadge
      badgeClassName="border-slate-300 bg-slate-50 text-slate-600"
      explanation={explanation}
      label={`Counted ${dates.formatShortDate(record.dateCredited ?? record.date)}`}
    />
  );
};

const ChurnedBadge = ({ record }: { record: PlacementDetailRecord }) => {
  const ended = record.dateEmploymentEnded
    ? `Employment ended ${formatDateOnly(record.dateEmploymentEnded)} within the backfill guarantee`
    : "Employment ended within the backfill guarantee";

  return (
    <PlacementFlagBadge
      badgeClassName="border-red-300 bg-red-50 text-red-700"
      explanation={`${ended} — the requisition still needs a backfill.`}
      label={
        record.dateEmploymentEnded
          ? `Churned ${dates.formatShortDate(record.dateEmploymentEnded)}`
          : "Churned"
      }
    />
  );
};

const PlacementsTable = ({
  onReassign,
  records,
  showOwner,
  stafferOptions,
}: {
  onReassign: (recordId: number, newUserId: number) => void;
  records: PlacementDetailRecord[];
  showOwner: boolean;
  stafferOptions: StafferOptions;
}) => (
  <Table className="text-slate-700">
    <TableHeader>
      <TableRow>
        <TableHead>Date</TableHead>
        <TableHead>Candidate</TableHead>
        <TableHead>Role</TableHead>
        <TableHead>Revenue</TableHead>
        <TableHead>Status</TableHead>
        {showOwner && <TableHead>Owner</TableHead>}
        <TableHead />
      </TableRow>
    </TableHeader>
    <TableBody>
      {records.map((r) => (
        <TableRow
          key={r.id}
          className={cn(
            r.companyId && r.jobId && r.candidacyId && "cursor-pointer",
            // Backfill re-fills credited to another period are context only
            !r.countsInPeriod && "opacity-60",
          )}
          onClick={() => {
            mixpanelUtils.trackRowClick(
              "Leaderboard candidacy",
              MIXPANEL_SCREENS.LEADERBOARD,
            );
            openCandidacy(r);
          }}
        >
          <TableCell>
            <Row className="items-center gap-1.5">
              {formatDateOnly(r.date)}
              {r.isBackfill && <BackfillBadge record={r} />}
              {r.isChurned && <ChurnedBadge record={r} />}
            </Row>
          </TableCell>
          <TableCell>
            <Row className="items-center gap-1.5">
              {r.candidateName ?? <EmptyDataDash />}
              <InternalSourceBadge source={r.candidacySource} />
            </Row>
          </TableCell>
          <TableCell className="max-w-[200px] truncate">
            {formatRole(r)}
          </TableCell>
          <TableCell>{formatting.currency(r.revenue)}</TableCell>
          <TableCell>
            <PlacementStatusBadge status={r.status} />
          </TableCell>
          {showOwner && (
            <TableCell>
              <OwnerAvatar
                ownerUserId={r.ownerUserId}
                stafferOptions={stafferOptions}
              />
            </TableCell>
          )}
          <TableCell
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <RowActionsDropdown
              onReassign={(newUserId) => {
                onReassign(r.id, newUserId);
              }}
              record={r}
              stafferOptions={stafferOptions}
            />
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

// Undetermined first, then accepted, declined, revoked.
const OFFER_OUTCOME_SECTIONS: { outcome: OfferOutcome; label: string }[] = [
  { outcome: OfferOutcome.UNDETERMINED, label: "Undetermined" },
  { outcome: OfferOutcome.ACCEPTED, label: "Accepted" },
  { outcome: OfferOutcome.DECLINED, label: "Declined" },
  { outcome: OfferOutcome.REVOKED, label: "Revoked" },
];

const OffersTable = ({
  onReassign,
  records,
  showOwner,
  stafferOptions,
}: {
  onReassign: (recordId: number, newUserId: number) => void;
  records: OfferDetailRecord[];
  showOwner: boolean;
  stafferOptions: StafferOptions;
}) => (
  <Table className="text-slate-700">
    <TableHeader>
      <TableRow>
        <TableHead>Date Offered</TableHead>
        <TableHead>Candidate</TableHead>
        <TableHead>Role</TableHead>
        <TableHead>Revenue</TableHead>
        <TableHead>Status</TableHead>
        {showOwner && <TableHead>Owner</TableHead>}
        <TableHead />
      </TableRow>
    </TableHeader>
    <TableBody>
      {OFFER_OUTCOME_SECTIONS.map(({ outcome, label }) => {
        const sectionRecords = records.filter((r) => r.outcome === outcome);
        if (sectionRecords.length === 0) return null;
        return (
          <Fragment key={outcome}>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableCell className="py-1.5" colSpan={showOwner ? 7 : 6}>
                <Text
                  className="text-xs uppercase text-slate-500"
                  variant="header"
                >
                  {label} ({sectionRecords.length})
                </Text>
              </TableCell>
            </TableRow>
            {sectionRecords.map((r) => (
              <TableRow
                key={r.id}
                className={cn(
                  r.companyId && r.jobId && r.candidacyId && "cursor-pointer",
                )}
                onClick={() => {
                  openCandidacy(r);
                }}
              >
                <TableCell>{formatDateOnly(r.date)}</TableCell>
                <TableCell>
                  <Row className="items-center gap-1.5">
                    {r.candidateName ?? <EmptyDataDash />}
                    <InternalSourceBadge source={r.candidacySource} />
                  </Row>
                </TableCell>
                <TableCell className="max-w-[200px] truncate">
                  {formatRole(r)}
                </TableCell>
                <TableCell>{formatting.currency(r.revenue)}</TableCell>
                <TableCell>
                  <PlacementStatusBadge status={r.status} />
                </TableCell>
                {showOwner && (
                  <TableCell>
                    <OwnerAvatar
                      ownerUserId={r.ownerUserId}
                      stafferOptions={stafferOptions}
                    />
                  </TableCell>
                )}
                <TableCell
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <RowActionsDropdown
                    onReassign={(newUserId) => {
                      onReassign(r.id, newUserId);
                    }}
                    record={r}
                    stafferOptions={stafferOptions}
                  />
                </TableCell>
              </TableRow>
            ))}
          </Fragment>
        );
      })}
    </TableBody>
  </Table>
);

const RowActionsDropdown = ({
  children,
  onReassign,
  record,
  stafferOptions,
  tab,
}: {
  children?: React.ReactNode;
  onReassign: (newUserId: number) => void;
  record: LeaderboardDetailRecord;
  stafferOptions: StafferOptions;
  tab?: string;
}) => {
  const companyId = record.companyId;
  const jobId = record.jobId;
  const candidacyId = record.candidacyId;
  const hasLink = companyId && jobId && candidacyId;
  const linkParams = hasLink ? new URLSearchParams({ c: candidacyId }) : null;
  if (linkParams && tab) linkParams.set("pt", tab);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className="h-auto w-auto p-0 hover:bg-transparent"
          size="icon"
          variant="ghost"
        >
          <MoreHorizontalIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {hasLink && linkParams && (
          <DropdownMenuItem asChild>
            <a
              href={`/companies/${companyId.toString()}/jobs/${jobId.toString()}?${linkParams.toString()}`}
              rel="noopener noreferrer"
              target="_blank"
            >
              <ExternalLinkIcon />
              View candidacy
            </a>
          </DropdownMenuItem>
        )}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <UserIcon />
            Reassign
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {stafferOptions.map((s) => (
              <DropdownMenuItem
                key={s.value}
                onClick={() => {
                  onReassign(s.value);
                }}
              >
                {s.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const StatusChangeTable = ({
  onReassign,
  records,
  showOwner,
  stafferOptions,
}: {
  onReassign: (recordId: number, newUserId: number) => void;
  records: StatusChangeDetailRecord[];
  showOwner: boolean;
  stafferOptions: StafferOptions;
}) => {
  const hasInterviews = records.some((r) => r.interviewDateTime !== null);

  return (
    <Table className="text-slate-700">
      <TableHeader>
        <TableRow>
          <TableHead>
            {hasInterviews ? "Interview Completed" : "Date"}
          </TableHead>
          <TableHead>Candidate</TableHead>
          <TableHead>Role</TableHead>
          {showOwner && <TableHead>Owner</TableHead>}
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {records.map((r) => (
          <TableRow
            key={r.id}
            className={cn(
              r.companyId && r.jobId && r.candidacyId && "cursor-pointer",
            )}
            onClick={() => {
              openCandidacy(r);
            }}
          >
            <TableCell>
              {hasInterviews ? formatInterviewDate(r) : formatDate(r.date)}
            </TableCell>
            <TableCell>
              <Row className="items-center gap-1.5">
                {r.candidateName ?? <EmptyDataDash />}
                <InternalSourceBadge source={r.candidacySource} />
              </Row>
            </TableCell>
            <TableCell className="max-w-[200px] truncate">
              {formatRole(r)}
            </TableCell>
            {showOwner && (
              <TableCell>
                <OwnerAvatar
                  ownerUserId={r.ownerUserId}
                  stafferOptions={stafferOptions}
                />
              </TableCell>
            )}
            <TableCell
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <RowActionsDropdown
                onReassign={(newUserId) => {
                  onReassign(r.id, newUserId);
                }}
                record={r}
                stafferOptions={stafferOptions}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const renderDetailTable = ({
  metric,
  records,
  showOwner,
  stafferOptions,
  onReassign,
  onAssignCandidacy,
}: {
  metric: LeaderboardMetric;
  records: LeaderboardDetailRecord[];
  showOwner: boolean;
  stafferOptions: StafferOptions;
  onReassign: (recordId: string | number, newUserId: number) => void;
  onAssignCandidacy: (callId: string, candidacyId: string) => void;
}) => {
  switch (metric) {
    case LeaderboardMetric.DIALS:
      return (
        <DialsTable
          records={records.filter(
            (r): r is CallDetailRecord =>
              r.type === LeaderboardDetailRecordType.CALL,
          )}
          showOwner={showOwner}
          stafferOptions={stafferOptions}
          onAssignCandidacy={onAssignCandidacy}
          onReassign={onReassign}
        />
      );
    case LeaderboardMetric.SCREENS:
    case LeaderboardMetric.SUBMITTALS:
    case LeaderboardMetric.INTERVIEWS:
      return (
        <StatusChangeTable
          records={records.filter(
            (r): r is StatusChangeDetailRecord =>
              r.type === LeaderboardDetailRecordType.STATUS_CHANGE,
          )}
          showOwner={showOwner}
          stafferOptions={stafferOptions}
          onReassign={onReassign}
        />
      );
    case LeaderboardMetric.OFFERS:
      return (
        <OffersTable
          records={records.filter(
            (r): r is OfferDetailRecord =>
              r.type === LeaderboardDetailRecordType.OFFER,
          )}
          showOwner={showOwner}
          stafferOptions={stafferOptions}
          onReassign={onReassign}
        />
      );
    case LeaderboardMetric.PLACEMENTS:
    case LeaderboardMetric.REVENUE:
      return (
        <PlacementsTable
          records={records.filter(
            (r): r is PlacementDetailRecord =>
              r.type === LeaderboardDetailRecordType.PLACEMENT,
          )}
          showOwner={showOwner}
          stafferOptions={stafferOptions}
          onReassign={onReassign}
        />
      );
  }
};

export const LeaderboardDetailSheet = ({
  dateRange,
  metric,
  metricLabel,
  onOpenChange,
  open,
  stafferOptions,
  timePeriod,
  userId,
  userName,
}: {
  dateRange?: DateRange;
  metric: LeaderboardMetric | null;
  metricLabel: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  stafferOptions: StafferOptions;
  timePeriod: DateFilterOption;
  userId: number | null;
  userName: string;
}) => {
  const { data, status } = useGetLeaderboardDetail({
    dateRange,
    metric,
    timePeriod,
    userId,
  });
  const queryClient = useQueryClient();
  const updateCallUserId = useUpdateCall();
  const updatePlacement = useUpdatePlacementById();
  const updateStatusChangeUserId = useUpdateCandidacyStatusChange();

  const invalidateLeaderboardQueries = async () => {
    await queryClient.invalidateQueries({ queryKey: ["leaderboard"] });
    await queryClient.invalidateQueries({ queryKey: ["leaderboard-detail"] });
  };

  const handleAssignCandidacy = (callId: string, candidacyId: string) => {
    mixpanelUtils.trackButtonClick(
      "Assign Candidacy",
      MIXPANEL_SCREENS.LEADERBOARD,
    );
    updateCallUserId.mutate(
      { callId, updateCallRequest: { candidacyId } },
      {
        onSuccess: () => {
          void invalidateLeaderboardQueries();
        },
      },
    );
  };

  const handleReassign = (recordId: string | number, newUserId: number) => {
    if (!metric || !data) return;
    mixpanelUtils.trackButtonClick(
      `Reassign: ${metric}`,
      MIXPANEL_SCREENS.LEADERBOARD,
    );

    if (metric === LeaderboardMetric.DIALS) {
      updateCallUserId.mutate(
        { callId: String(recordId), updateCallRequest: { userId: newUserId } },
        {
          onSuccess: () => {
            void invalidateLeaderboardQueries();
          },
        },
      );
    } else if (
      metric === LeaderboardMetric.SCREENS ||
      metric === LeaderboardMetric.SUBMITTALS ||
      metric === LeaderboardMetric.INTERVIEWS
    ) {
      const record = data.records.find((r) => r.id === recordId);
      if (record?.companyId && record.jobId && record.candidacyId) {
        updateStatusChangeUserId.mutate(
          {
            companyId: record.companyId,
            jobId: record.jobId,
            candidacyId: record.candidacyId,
            candidacyStatusChangeId: Number(recordId),
            updateCandidacyStatusChangeRequest: { userId: newUserId },
          },
          {
            onSuccess: () => {
              void invalidateLeaderboardQueries();
            },
          },
        );
      }
    } else {
      const record = data.records.find((r) => r.id === recordId);
      if (record?.companyId && record.jobId) {
        updatePlacement.mutate(
          {
            companyId: record.companyId,
            jobId: record.jobId,
            id: Number(recordId),
            updatePlacementRequest: { ownerUserId: newUserId },
          },
          {
            onSuccess: () => {
              void invalidateLeaderboardQueries();
            },
          },
        );
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "flex max-h-[85vh] flex-col overflow-hidden transition-[max-width] duration-300 ease-in-out",
          metric === LeaderboardMetric.DIALS ? "max-w-[90vw]" : "max-w-6xl",
        )}
      >
        <DialogHeader>
          <DialogTitle className="text-slate-700">
            {userName}&apos;s {metricLabel}
          </DialogTitle>
          <DialogDescription>{dateFilterToLabel[timePeriod]}</DialogDescription>
        </DialogHeader>
        <Column className="min-h-0 flex-1 overflow-auto">
          {status === "pending" ? (
            <LoadingIndicator className="py-12" variant="medium" />
          ) : status === "error" ? (
            <p className="text-sm text-slate-500">
              Unable to load detail records.
            </p>
          ) : data.records.length === 0 ? (
            <p className="text-sm text-slate-500">
              No records for this period.
            </p>
          ) : metric ? (
            renderDetailTable({
              metric,
              records: data.records,
              showOwner: userId === null,
              stafferOptions,
              onReassign: handleReassign,
              onAssignCandidacy: handleAssignCandidacy,
            })
          ) : null}
        </Column>
      </DialogContent>
    </Dialog>
  );
};
